<script setup lang="ts">
import type {
  FormBlock as FormBlockType,
  FormFieldData,
  ViewportSize,
} from "@aswin.dev/types";
import { computed } from "vue";

const props = defineProps<{
  block: FormBlockType;
  viewport: ViewportSize;
}>();

function hasVisibleLabel(field: FormFieldData): boolean {
  return Boolean(field.label?.trim());
}

/** When there is no visible label element, expose a short name for screen readers. */
function inputAriaLabel(field: FormFieldData): string | undefined {
  if (hasVisibleLabel(field)) return undefined;
  const p = field.placeholder?.trim();
  if (p) return p;
  const n = field.name?.trim();
  if (n) return n;
  if (field.type === "email") return "Email";
  if (field.type === "phone") return "Phone";
  return "Input";
}

function spacingCss(s: {
  top: number;
  right: number;
  bottom: number;
  left: number;
}): string {
  return `${s.top}px ${s.right}px ${s.bottom}px ${s.left}px`;
}

function inputType(field: FormFieldData): string {
  if (field.type === "email") return "email";
  if (field.type === "phone") return "tel";
  return "text";
}

const labelStyle = computed(
  (): Record<string, string> => ({
    display: "block",
    marginBottom: "6px",
    fontSize: `${props.block.labelFontSize}px`,
    color: props.block.labelColor,
    fontWeight: "500",
  }),
);

const inputStyle = computed(
  (): Record<string, string> => ({
    boxSizing: "border-box",
    display: "block",
    width: "100%",
    padding: spacingCss(props.block.inputPadding),
    fontSize: `${props.block.inputFontSize}px`,
    color: props.block.inputTextColor,
    backgroundColor: props.block.inputBackgroundColor,
    borderRadius: `${props.block.inputBorderRadius}px`,
    border:
      props.block.inputBorderWidth > 0
        ? `${props.block.inputBorderWidth}px solid ${props.block.inputBorderColor}`
        : "none",
    outline: "none",
  }),
);

const stackGap = computed(() => `${props.block.inputSpacing}px`);

const buttonStyle = computed(
  (): Record<string, string> => ({
    display: "block",
    width: "100%",
    padding: spacingCss(props.block.inputPadding),
    fontSize: `${props.block.buttonFontSize}px`,
    color: props.block.buttonTextColor,
    backgroundColor: props.block.buttonBackgroundColor,
    borderRadius: `${props.block.buttonBorderRadius}px`,
    border: "none",
    fontWeight: "600",
    cursor: "pointer",
    textAlign: "center",
  }),
);

const placeholderHex = computed(() => props.block.inputPlaceholderColor);
const formId = computed(() => `tpl-form-${props.block.id}`);

const buttonRowStyle = computed((): Record<string, string> => {
  if (props.block.buttonLayout === "two_lines") {
    return { display: "block", marginTop: stackGap.value };
  }
  return {
    display: "flex",
    gap: stackGap.value,
    alignItems: "stretch",
    marginTop: stackGap.value,
  };
});
</script>

<template>
  <div :id="formId" class="tpl-form-root tpl:w-full">
    <div
      v-if="block.fields.length === 0"
      class="tpl:rounded-md tpl:border tpl:border-dashed tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg)] tpl:p-6 tpl:text-center tpl:text-sm tpl:text-[var(--tpl-text-muted)]"
    >
      Add a field from the Fields tab to begin.
    </div>

    <div
      v-else
      class="tpl-form-stack"
      :style="{
        display: 'flex',
        flexDirection: 'column',
        gap: stackGap,
      }"
    >
      <div
        v-for="field in block.fields"
        :key="field.id"
        class="tpl-form-field tpl:flex tpl:flex-col tpl:gap-1"
      >
        <label
          v-if="hasVisibleLabel(field)"
          :for="`${field.id}-input`"
          class="tpl-form-label tpl:block tpl:leading-snug"
          :style="labelStyle"
        >
          {{ field.label.trim()
          }}<span
            v-if="field.required"
            class="tpl:ml-1 tpl:text-[var(--tpl-danger)]"
            >*</span
          >
        </label>
        <input
          :id="`${field.id}-input`"
          class="tpl-form-input"
          :type="inputType(field)"
          :name="field.name"
          :placeholder="field.placeholder"
          :aria-label="inputAriaLabel(field)"
          value=""
          :required="field.required"
          readonly
          tabindex="-1"
          :style="inputStyle"
        />
      </div>
    </div>

    <div :style="buttonRowStyle">
      <button type="button" :style="buttonStyle" tabindex="-1">
        {{ block.submitButtonText }}
      </button>
    </div>

    <div
      v-if="block.termsEnabled && block.termsContent"
      class="tpl:mt-3 tpl:text-xs tpl:text-[var(--tpl-text-muted)] tpl:leading-snug"
      v-html="block.termsContent"
    />
  </div>
</template>

<style scoped>
.tpl-form-root {
  --tpl-form-ph: v-bind(placeholderHex);
}
.tpl-form-input::placeholder {
  color: var(--tpl-form-ph);
  opacity: 1;
}
</style>
